<template>
  <div
    v-on-click-outside="onClickOutside"
    :class="[
      'filter-block',
      'filter-block-range',
      { open },
      { 'filter-block-set': isSetFilter },
    ]"
  >
    <div ref="wrapper" class="wrapper">
      <div class="filter-block-header" tabindex="0" @click="onHeaderClick">
        <slot name="header" :open="open">
          <span class="title-header body-m-regular">{{ title }}</span>
        </slot>
      </div>
      <div
        ref="listBody"
        class="filter-block-list-wrapper"
        @click="onClickListWrap"
      >
        <div class="filter-block-list-effect">
          <div class="popup-title text-h3-bold-up">
            Фильтры
            <div class="icon-close" @click="onPopup(false)" />
          </div>
          <div class="filter-block-list-header">
            <div class="filter-block-list-title body-m-regular">
              {{ settings?.name
              }}{{ settings.unit ? ', ' + settings.unit : '' }}
            </div>
            <button
              type="button"
              class="reset-button body-m-regular"
              :disabled="disabled"
              @click="onReset"
            >
              Сбросить
            </button>
          </div>
          <div class="range-wrap">
            <range-input
              v-bind="$attrs"
              :settings="settings"
              :value="value"
              @on-change="onChange"
              @get-filters="onSetFilters"
            />
          </div>
          <div class="button-wrap body-m-regular">
            <ui-ui-button
              size="medium"
              font-class="body-m-regular"
              :disabled="disabled"
              :type="disabled ? 'neutral' : 'primary'"
              :resize="true"
              @click="onSetFilters"
            >
              Применить
            </ui-ui-button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { vOnClickOutside } from '@vueuse/components';
import type { EventPatch, RangeFilter } from '~/interfaces/api';
import type { RangeFilteringValue } from '~/interfaces/app';
import { useMediaQueryStore } from '~/stores/mediaQuery';
import RangeInput from '~/components/filters/range-input/range-input.vue';

interface ChangeValue {
  id: number;
  queryVal: string;
  val: {
    max: null | number;
    min: null | number;
  };
}

const props = defineProps({
  settings: {
    type: Object as () => RangeFilter,
    required: true,
  },
  value: {
    type: Object as () => RangeFilteringValue,
    required: true,
  },
  setFilters: {
    type: String,
    default: '',
  },
});
const emit = defineEmits(['onRangeChange', 'setFilters']);
const mediaStore = useMediaQueryStore();
const open = ref(false);
const isSetFilter = ref(false);
const listBody = ref<HTMLElement>();
const wrapper = ref<HTMLElement>();
const isTablet = computed(
  () =>
    mediaStore.isMobile || mediaStore.isEs || mediaStore.isSm || mediaStore.isMd
);
const isEquallyValue = () => {
  const minSettings = props.settings.min_value;
  const maxSettings = props.settings.max_value;
  const min = props.value?.min;
  const max = props.value?.max;
  if (!(min && max) || !(minSettings && maxSettings)) return true;
  return min === minSettings && max === maxSettings;
};
const getIsDisabled = () => {
  if (open.value && isSetFilter.value) return false;
  return isEquallyValue();
};

const disabled = computed(() => {
  return getIsDisabled();
});

const title = computed(() => {
  const { value, settings } = props;
  const { min_value, max_value, name, unit } = settings;

  if (!value?.min && !value?.max) {
    return name || '';
  }

  const min = value?.min || min_value;
  const max = value?.max || max_value;
  const prefix = unit ? ' ' + unit : '';

  return isSetFilter.value ? `${min}${prefix} - ${max}${prefix}` : name || '';
});

watch(
  () => isTablet.value,
  () => {
    onPopup(false);
  }
);

const popupLeft = computed(() => {
  if (!isTablet.value && open.value && wrapper.value && listBody.value) {
    const { width: listWidth } = listBody.value.getBoundingClientRect();
    const { width: wrapperWidth, left: wrapperLeft } =
      wrapper.value.getBoundingClientRect();

    if (wrapperLeft + listWidth >= document.body.clientWidth) {
      return `-${listWidth - wrapperWidth}px`;
    }
  }
  return '0';
});

watch(
  () => props.setFilters,
  (value) => {
    if (value) {
      setTimeout(() => {
        isSetFilter.value = !getIsDisabled();
      });
    }
  }
);

const onPopup = (state: boolean): void => {
  if (state) {
    open.value = true;
    nextTick(() => {
      if (listBody.value) {
        const scrollWidth =
          listBody.value?.offsetWidth - listBody.value?.clientWidth;
        document.body.classList.add('tablet-overflow-hidden-grid');
        document.body.style.paddingRight = scrollWidth + 'px';
      }
    });
  } else {
    open.value = false;
    document.body.classList.remove('tablet-overflow-hidden-grid');
    document.body.style.paddingRight = '';
  }
};

const onHeaderClick = (): void => {
  if (isTablet.value) onPopup(true);
  else open.value = !open.value;
};

const onClickOutside = (): void => {
  if (!isTablet.value) open.value = false;
};

const onReset = (): void => {
  onPopup(false);
  emit('onRangeChange', {
    id: props.settings.id,
    val: {
      max: props.settings.max_value,
      min: props.settings.min_value,
    },
    queryVal: undefined,
  });
  isSetFilter.value = false;
  emit('setFilters');
};

const onSetFilters = (): void => {
  if (isEquallyValue()) {
    onReset();
  } else {
    onPopup(false);
    isSetFilter.value = true;
    emit('setFilters');
  }
};

const onClickListWrap = (e: EventPatch) => {
  const className = 'filter-block-list-wrapper';
  const target = e.target as HTMLElement;
  if (
    (e && 'path' in e && e.path[0] && e?.path[0]?.className === className) ||
    // ios safari fix
    target?.className === className
  )
    onPopup(false);
};

const onChange = (value: ChangeValue) => {
  emit('onRangeChange', {
    id: props.settings.id,
    val: value.val,
    queryVal: Object.values(value.val).join(','),
  });
};
onMounted(() => {
  isSetFilter.value = !getIsDisabled();
});
</script>

<style scoped lang="scss">
.filter-block {
  position: relative;
  width: auto;
  max-width: 250px;
  display: inline-block;
  background: $achromatic-card-bg;
  border-radius: 8px;

  &.wrapper {
    display: flex;
    flex-flow: column nowrap;
  }

  &-header {
    display: block;
    width: auto;
    cursor: pointer;
    user-select: none;
    outline: none;
    box-shadow: none;
    position: relative;
  }

  .search-wrap {
    padding: 8px 12px;
  }

  .no-search {
    padding: 0 12px;
  }

  .search {
    height: 36px;

    :deep(.p-input) {
      padding: 7px 11px;
    }

    .icon {
      padding-right: 8px;
      object-fit: contain;
      height: 20px;
    }
  }

  .filter-block-list-wrapper {
    z-index: 20;
    position: fixed;
    bottom: 0;
    left: 0;
    width: 100vw;
    background: rgba(0, 0, 0, 0);
    height: 0;
    overflow-y: scroll;
    align-self: center;
    transition: background-color 0.2s;
    border-width: 0;
    box-shadow: 0 8px 16px rgba(51, 51, 51, 0.2);
    border-radius: 4px;

    .filter-block-list-effect {
      background-color: $white;
      border-radius: 4px;
      @include lg-block {
        transform: translateY(100%);
      }
    }

    .filter-block-list-header {
      margin-top: 16px;
      display: flex;
      justify-content: space-between;
      padding: 0 12px;
      background: $white;
      @include lg-block {
        margin-top: 0;
      }
    }

    .filter-block-list-title {
      max-width: calc(100% - 65px);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      letter-spacing: 0.01em;
      color: $achromatic-black;
      @include lg-block {
        color: $achromatic-disabled;
      }
    }

    .reset-button {
      max-width: 65px;
      cursor: pointer;
      background: none;
      border: none;
      padding: 0;
      outline: none;
      color: $achromatic-disabled;

      &:disabled {
        color: #bebebe;
        cursor: default;
      }
    }

    .popup-title {
      margin-top: 12px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 0 12px;
      position: static;
      top: 0;
      z-index: 1;
      background-color: $white;

      .icon-close {
        width: 20px;
        height: 20px;
        cursor: pointer;
        mask: url('/static/images/closeModal.svg') center / cover no-repeat;
        background-color: $achromatic-black;

        &:hover {
          background-color: $main-cta;
        }

        &:active {
          background-color: $hover-cta;
        }
      }

      @include lg-block {
        display: none;
      }
    }

    .filter-block-list {
      padding-top: 8px;
      user-select: none;
      display: flex;
      flex-flow: column nowrap;
      width: 100%;
      max-height: 250px;
      background: $white;
      @include lg-block {
        background: initial;
      }

      .select-item {
        cursor: pointer;
        padding: 6px 12px;
        color: $achromatic-black;
        border-radius: 0;

        .item-checkbox {
          pointer-events: none;
        }

        &.active {
          justify-content: flex-start;
        }

        @include lg-block {
          border-radius: 4px;
          &:hover {
            background-color: $light-blue;
          }
        }
      }
    }

    .button-wrap {
      padding: 10px 12px 12px;
      background: $white;
      @include lg-block {
        padding-top: 12px;
      }
    }

    @include lg-block {
      position: absolute;
      width: 316px;
      overflow: hidden;
      background: $white;
    }
  }

  .filter-block-header {
    border-radius: 4px;
    padding: 8px 12px;
    display: flex;
    align-items: center;
    color: $achromatic-black;

    .title-header {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    &:after {
      content: '';
      display: block;
      mask: url('/static/images/icon/triangle-filter.svg') center / contain
        no-repeat;
      width: 16px;
      height: 16px;
      margin-left: 4px;
      background-color: $achromatic-black;
      flex-shrink: 0;
      transform: translateY(-1px);
      transition: transform 0.3s ease-in;
      @include lg-block {
        width: 20px;
        height: 20px;
      }
    }

    &:active {
      background: $light-blue;
      border-radius: 8px;
    }

    &:hover {
      @include lg-block {
        background: $light-blue;
      }
    }
  }

  &.open {
    .filter-block-header {
      background: $light-blue;
      border-radius: 8px;

      &:after {
        transform: rotate(180deg) translateY(0px);
      }
    }
  }

  &-set {
    border: 1px solid $main-cta;
  }

  &-list-wrapper {
    opacity: 1;

    .filter-block-list {
      border-radius: 0;
    }

    transition: opacity 0.2s;
    top: 100%;
    @include lg-block {
      opacity: 0;
      .filter-block-list {
        border-radius: 4px;
      }
    }
  }

  &.open {
    .filter-block-list-wrapper {
      right: 0;
      left: v-bind(popupLeft);
      top: 0;
      bottom: 0;
      padding-top: 98px;
      height: var(--fullvh);
      opacity: 1;
      z-index: 100;
      overflow: initial;
      display: flex;
      flex-direction: column;
      justify-content: flex-end;
      align-items: stretch;
      background-color: rgba(0, 0, 0, 0.6);
      transition: background-color 0.2s;

      .filter-block-list-effect {
        transform: translateY(0);
      }

      @include lg-block {
        top: 35px;
        bottom: initial;
        background-color: initial;
        border: 1px solid $achromatic-hover;
        padding-top: 8px;
        height: auto;
        opacity: 1;
        z-index: 40;
        overflow: initial;
      }
    }
  }

  .range-wrap {
    padding: 12px;
  }
}
</style>
